class FsrFreeGifts extends HTMLElement {
  constructor() {
    super();
    this.boundHandleCartChange = this._handleCartChange.bind(this);
    this.isUpdating = false; // Verhindert doppelte API-Calls während des Ladens
  }

  connectedCallback() {
    this._attachSelectors();
    if (!window.listenersAttached) {
      this._attachListeners();
      window.listenersAttached = true;
    }
    // Wir rufen diese initial nicht auf, da uns die cart.items fehlen.
    // Idealerweise feuerst du beim Laden der Seite initial ein "cart:change" Event.
    this._updateProgressBar();
    this._updateFreeGiftText(); 
  }

  _attachSelectors() {
    this.cartTotal = this.getAttribute("cart-total");
    this.thresholdFreeShipping = this.getAttribute("threshold_one");
    this.thresholdTwo = this.getAttribute("threshold_two");
    this.thresholdThree = this.getAttribute("threshold_three");
    this.freeGiftIdOne = this.getAttribute("free-gift-id-1");
    this.freeGiftIdTwo = this.getAttribute("free-gift-id-2");
    this.progressBar = document.querySelector(".free-gift-progress");
    this.freeGiftText = document.querySelector(".free-gift-text");
  }

  _attachListeners() {
    document.documentElement.addEventListener(
      "cart:change",
      this.boundHandleCartChange
    );
    document.documentElement.addEventListener(
      "fsr:cart:updated",
      this.boundHandleCartChange
    );
  }

  // _removeListeners() wurde entfernt, da wir dauerhaft auf Änderungen hören wollen

  _handleCartChange(event) {
    if (this.isUpdating) return; // Blockiert, wenn gerade hinzugefügt/entfernt wird

    const cart = event.detail.cart;
    if (!cart) return;

    this.cartTotal = cart.total_price;
    this._updateProgressBar();
    this._updateFreeGiftText();
    
    // Übergebe die echten Warenkorb-Items an die Logik
    this._updateCart(this.cartTotal, cart.items || []);
  }

  _updateCart(cartTotal, cartItems) {
    // Hole alle IDs, die aktuell wirklich im Warenkorb sind (als String für sicheren Vergleich)
    const itemIdsInCart = cartItems.map(item => String(item.id || item.variant_id));
    
    const hasGiftOne = itemIdsInCart.includes(this.freeGiftIdOne);
    const hasGiftTwo = itemIdsInCart.includes(this.freeGiftIdTwo);

    let giftsToAdd = [];
    let giftsToRemove = [];

    // Logik basierend auf Thresholds evaluieren
    if (cartTotal >= parseFloat(this.thresholdThree)) {
      if (!hasGiftOne) giftsToAdd.push(this.freeGiftIdOne);
      if (!hasGiftTwo) giftsToAdd.push(this.freeGiftIdTwo);
    } else if (cartTotal >= parseFloat(this.thresholdTwo)) {
      if (!hasGiftOne) giftsToAdd.push(this.freeGiftIdOne);
      if (hasGiftTwo) giftsToRemove.push(this.freeGiftIdTwo); // Zweites Geschenk entfernen
    } else {
      if (hasGiftOne) giftsToRemove.push(this.freeGiftIdOne);
      if (hasGiftTwo) giftsToRemove.push(this.freeGiftIdTwo);
    }

    // Aktionen nur ausführen, wenn sich wirklich etwas ändern muss
    if (giftsToAdd.length > 0) {
      this._onSubmit(giftsToAdd);
    } else if (giftsToRemove.length > 0) {
      this._onRemove(giftsToRemove);
    }
  }

  _onSubmit(giftIdsToAdd) {
    this.isUpdating = true; // Sperre aktivieren

    let sectionsToBundle = ["variant-added"];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const formData = new FormData();
    formData.set("sections", sectionsToBundle.join(","));

    giftIdsToAdd.forEach((giftId, i) => {
      formData.append(`items[${i}][id]`, giftId);
      formData.append(`items[${i}][quantity]`, "1");
      formData.append(`items[${i}][properties][_free-gift]`, "true");
    });

    fetch("/cart/add.js", {
      body: formData,
      method: "POST",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok");
        return response.json();
      })
      .then((cart) => {
        this.isUpdating = false; // Sperre aufheben BEVOR das Event fliegt
        document.dispatchEvent(
          new CustomEvent("fsr:gift:added", {
            bubbles: true,
            detail: { cart: cart },
          })
        );
      })
      .catch((e) => {
        this.isUpdating = false;
        console.error("Error adding gift:", e);
      });
  }

  async _onRemove(giftIdsToRemove) {
    this.isUpdating = true; // Sperre aktivieren

    for (let giftId of giftIdsToRemove) {
      let sectionsToBundle = ["variant-added"];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );
      
      const formData = new FormData();
      formData.set("sections", sectionsToBundle.join(","));
      formData.append("id", giftId);
      formData.append("quantity", "0");

      try {
        const response = await fetch("/cart/change.js", {
          body: formData,
          method: "POST",
          headers: { "X-Requested-With": "XMLHttpRequest" },
        });

        if (!response.ok) throw new Error("Network response was not ok");
        const cart = await response.json();

        // Optional: Kurzer Delay bei mehreren Removes, wie in deinem Originalcode
        await new Promise((resolve) => setTimeout(resolve, 300));

        this.isUpdating = false; // Sperre aufheben
        document.dispatchEvent(
          new CustomEvent("fsr:gift:added", { // Vielleicht das Event "fsr:gift:removed" nennen?
            bubbles: true,
            detail: { cart: cart },
          })
        );
      } catch (error) {
        this.isUpdating = false;
        console.error("Error removing gift:", error);
      }
    }
  }

  _updateProgressBar() {
    const cartTotalCents = parseFloat(this.cartTotal);
    const thresholdOneCents = parseFloat(this.thresholdFreeShipping);
    const thresholdTwoCents = parseFloat(this.thresholdTwo);
    const thresholdThreeCents = parseFloat(this.thresholdThree);
    const freeGiftItems = document.querySelectorAll(".free-gift-item");

    let progressBarWidth;

    if (cartTotalCents < thresholdOneCents) {
      progressBarWidth = (cartTotalCents / thresholdOneCents) * 30;
      freeGiftItems.forEach((item) => (item.style.opacity = "0.5"));
    } else if (cartTotalCents < thresholdTwoCents) {
      const percentageBetweenThresholds =
        (cartTotalCents - thresholdOneCents) /
        (thresholdTwoCents - thresholdOneCents);
      progressBarWidth = 33 + percentageBetweenThresholds * (66 - 33);
      freeGiftItems.forEach(
        (item, index) => (item.style.opacity = index < 1 ? "1" : "0.5")
      );
    } else if (cartTotalCents < thresholdThreeCents) {
      const percentageBetweenThresholds =
        (cartTotalCents - thresholdTwoCents) /
        (thresholdThreeCents - thresholdTwoCents);
      progressBarWidth = 66 + percentageBetweenThresholds * (100 - 66);
      freeGiftItems.forEach(
        (item, index) => (item.style.opacity = index < 2 ? "1" : "0.5")
      );
    } else {
      progressBarWidth = 100;
      freeGiftItems.forEach((item) => (item.style.opacity = "1"));
    }

    this.progressBar.style.width = `${progressBarWidth}%`;
  }

  _updateFreeGiftText() {
    const cartTotalCents = parseFloat(this.cartTotal);
    let remainingAmount;
    let message;

    function formatPrice(amount) {
      return amount.toFixed(2).replace(".", ",");
    }

    if (cartTotalCents < parseFloat(this.thresholdFreeShipping)) {
      remainingAmount =
        (parseFloat(this.thresholdFreeShipping) - cartTotalCents) / 100;
      message = `Es fehlen noch ${formatPrice(
        remainingAmount
      )}€ für kostenlosen Versand!`;
    } else if (cartTotalCents < parseFloat(this.thresholdTwo)) {
      remainingAmount = (parseFloat(this.thresholdTwo) - cartTotalCents) / 100;
      message = `Es fehlen noch ${formatPrice(
        remainingAmount
      )}€ für eine gratis Saatgut-Box!`;
    } else if (cartTotalCents < parseFloat(this.thresholdThree)) {
      remainingAmount =
        (parseFloat(this.thresholdThree) - cartTotalCents) / 100;
      message = `Es fehlen noch ${formatPrice(
        remainingAmount
      )}€ für einen gratis Feuchtigkeitsmesser!`;
    } else {
      message = "Alle Belohnungen freigeschaltet!";
    }

    this.freeGiftText.textContent = message;
  }
}

customElements.define("fsr-free-gifts", FsrFreeGifts);
